import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useAuthContext } from '../../context/Context';
import { getSubjectById } from '../../services/exams.util';
import { getMaterialBySlug } from '../../services/materials.util';
import { getSubjectByIdForMockTest } from '../../services/mockTests.util';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHome, 
  faBook, 
  faGraduationCap, 
  faTachometerAlt, 
  faUser, 
  faEnvelope, 
  faInfoCircle,
  faBookOpen,
  faChartBar
} from '@fortawesome/free-solid-svg-icons';

export default function DynamicBreadcrumb() {
  const location = useLocation();
  const pathname = location.pathname;
  const { loggedIn } = useAuthContext();
  const [subjectTitle, setSubjectTitle] = useState(null);
  const [materialTitle, setMaterialTitle] = useState(null);

  // Helper function to get subject display name
  const getExamDisplayName = (code) => {
    switch(code?.toLowerCase()) {
      case 'rrb':
        return 'Railway Recruitment Board (RRB)';
      case 'di':
        return 'Data Interpretation (DI)';
      case 'pharmacology':
      case 'pharmacologist':
        return 'Pharmacology';
      default:
        return code;
    }
  };

  // Helper function to get appropriate icon for breadcrumb item
  const getIconForBreadcrumb = (title, target) => {
    if (target === '/') return faHome;
    if (target.startsWith('/exam/')) {
      const examCode = target.split('/')[2]?.toLowerCase();
      switch(examCode) {
        case 'rrb': return faGraduationCap;
        case 'di': return faChartBar;
        case 'pharmacology':
        case 'pharmacologist': return faBook;
        default: return faGraduationCap;
      }
    }
    if (target.startsWith('/subject/')) {
      return faBookOpen;
    }
    if (target.startsWith('/materials')) {
      return faBookOpen;
    }
    if (target.startsWith('/volumes')) return faBookOpen;
    if (target.startsWith('/courses')) return faGraduationCap;
    if (target.startsWith('/dashboard')) return faTachometerAlt;
    if (target.includes('about')) return faInfoCircle;
    if (target.includes('contact')) return faEnvelope;
    if (target.includes('user-settings')) return faUser;
    return faBook; // default icon
  };

  // Fetch subject title and material title when on a subject page
  useEffect(() => {
    const pathSegments = pathname.split('/').filter(segment => segment);
    
    // Handle exam routes with subjects
    if (pathSegments[0] === 'exam' && pathSegments[2] === 'subject' && pathSegments[3]) {
      const subjectId = pathSegments[3];
      
      const fetchSubjectTitle = async () => {
        try {
          const subjectData = await getSubjectById(subjectId);
          setSubjectTitle(subjectData.title);
        } catch (error) {
          console.error('Error fetching subject title:', error);
          setSubjectTitle(`Subject ${subjectId.slice(-4)}`); // Fallback
        }
      };

      fetchSubjectTitle();
      setMaterialTitle(null);
    } 
    // Handle materials routes with subjects
    else if (pathSegments[0] === 'materials' && pathSegments[2] === 'subject' && pathSegments[3]) {
      const materialSlug = pathSegments[1];
      const subjectId = pathSegments[3];
      
      const fetchData = async () => {
        try {
          // Fetch material title
          const materialData = await getMaterialBySlug(materialSlug);
          setMaterialTitle(materialData.title);
          
          // Fetch subject title
          const subjectData = await getSubjectById(subjectId);
          setSubjectTitle(subjectData.title);
        } catch (error) {
          console.error('Error fetching material/subject data:', error);
          setMaterialTitle(pathSegments[1]); // Fallback to slug
          setSubjectTitle(`Subject ${subjectId.slice(-4)}`); // Fallback
        }
      };

      fetchData();
    }
    // Handle mock-tests routes with subjects
    else if (pathSegments[0] === 'mock-tests' && pathSegments[2] === 'subject' && pathSegments[3]) {
      const subjectId = pathSegments[3];
      
      const fetchSubjectTitle = async () => {
        try {
          const subjectData = await getSubjectByIdForMockTest(subjectId);
          setSubjectTitle(subjectData.title);
        } catch (error) {
          console.error('Error fetching subject title:', error);
          setSubjectTitle(`Subject ${subjectId.slice(-4)}`); // Fallback
        }
      };

      fetchSubjectTitle();
      setMaterialTitle(null);
    } else {
      setSubjectTitle(null);
      setMaterialTitle(null);
    }
  }, [pathname]);

  // Don't show breadcrumbs if user is not logged in
  if (!loggedIn) {
    return null;
  }

  // Generate breadcrumb items based on current route
  const generateBreadcrumbs = () => {
    const breadcrumbs = [
      { title: 'Home', target: '/', icon: faHome }
    ];

    // Split the pathname and filter out empty strings
    const pathSegments = pathname.split('/').filter(segment => segment);

    if (pathSegments.length === 0) {
      // We're on home page, just return Home
      return breadcrumbs;
    }

    // Handle specific routes
    if (pathSegments[0] === 'exam' && pathSegments[1]) {
      // Exam pages: /exam/RRB -> Home / RRB
      const examName = getExamDisplayName(pathSegments[1]);
      const examTarget = `/exam/${pathSegments[1]}`;
      breadcrumbs.push({
        title: examName,
        target: examTarget,
        icon: getIconForBreadcrumb(examName, examTarget)
      });
      
      // If it's a subject under exam: /exam/RRB/subject/subject-1 -> Home / RRB / Subject Title
      if (pathSegments[2] === 'subject' && pathSegments[3]) {
        const subjectTarget = `/exam/${pathSegments[1]}/subject/${pathSegments[3]}`;
        const title = subjectTitle || `Subject ${pathSegments[3].slice(-4)}`;
        breadcrumbs.push({
          title: title,
          target: subjectTarget,
          icon: faBookOpen
        });
      }
    } else if (pathSegments[0] === 'materials') {
      // Materials pages: /materials -> Home / Materials
      breadcrumbs.push({
        title: 'Materials',
        target: '/materials',
        icon: faBookOpen
      });
      
      // If it's a material detail page: /materials/my-first-materials -> Home / Materials / Material Title
      if (pathSegments[1] && pathSegments[2] !== 'subject') {
        const materialTarget = `/materials/${pathSegments[1]}`;
        const title = materialTitle || pathSegments[1].replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        breadcrumbs.push({
          title: title,
          target: materialTarget,
          icon: faBookOpen
        });
      }
      
      // If it's a subject under material: /materials/my-first-materials/subject/subject-id -> Home / Materials / Material Title / Subject Title
      // Skip the "subject" segment and show subject title instead of ID
      if (pathSegments[2] === 'subject' && pathSegments[3]) {
        // Add material breadcrumb if not already added
        if (pathSegments[1] && !breadcrumbs.some(b => b.target === `/materials/${pathSegments[1]}`)) {
          const materialTarget = `/materials/${pathSegments[1]}`;
          const materialName = materialTitle || pathSegments[1].replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          breadcrumbs.push({
            title: materialName,
            target: materialTarget,
            icon: faBookOpen
          });
        }
        
        // Add subject breadcrumb (skip "subject" segment, use subject title)
        const subjectTarget = `/materials/${pathSegments[1]}/subject/${pathSegments[3]}`;
        const title = subjectTitle || `Subject ${pathSegments[3].slice(-4)}`;
        breadcrumbs.push({
          title: title,
          target: subjectTarget,
          icon: faBookOpen
        });
      }
    } else if (pathSegments[0] === 'volumes') {
      // Volumes pages: /volumes/volume1 -> Home / Volumes / Volume1
      breadcrumbs.push({
        title: 'Volumes',
        target: '/volumes',
        icon: faBookOpen
      });
      if (pathSegments[1]) {
        breadcrumbs.push({
          title: pathSegments[1].toUpperCase(),
          target: `/volumes/${pathSegments[1]}`,
          icon: faBook
        });
      }
      if (pathSegments[2]) {
        const target = `/volumes/${pathSegments[1]}/${pathSegments[2]}`;
        breadcrumbs.push({
          title: getExamDisplayName(pathSegments[2]),
          target: target,
          icon: getIconForBreadcrumb(pathSegments[2], target)
        });
      }
    } else if (pathSegments[0] === 'courses') {
      // Course pages: /courses/volume1/subject1 -> Home / Courses / Volume1 / Subject1
      breadcrumbs.push({
        title: 'Courses',
        target: '/courses',
        icon: faGraduationCap
      });
      if (pathSegments[1]) {
        breadcrumbs.push({
          title: pathSegments[1].toUpperCase(),
          target: `/courses/${pathSegments[1]}`,
          icon: faBook
        });
      }
      if (pathSegments[2]) {
        const target = `/courses/${pathSegments[1]}/${pathSegments[2]}`;
        breadcrumbs.push({
          title: getExamDisplayName(pathSegments[2]),
          target: target,
          icon: getIconForBreadcrumb(pathSegments[2], target)
        });
      }
    } else if (pathSegments[0] === 'mock-tests') {
      // Mock tests pages: /mock-tests -> Home / Mock tests
      breadcrumbs.push({
        title: 'Mock tests',
        target: '/mock-tests',
        icon: faBookOpen
      });
      
      // If it's a mock test detail page: /mock-tests/test-on-materials -> Home / Mock tests / Test on-materials
      if (pathSegments[1] && pathSegments[2] !== 'subject') {
        const mockTestTarget = `/mock-tests/${pathSegments[1]}`;
        const mockTestTitle = pathSegments[1].replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        breadcrumbs.push({
          title: mockTestTitle,
          target: mockTestTarget,
          icon: faBookOpen
        });
      }
      
      // If it's a subject under mock test: /mock-tests/test-on-materials/subject/subject-id -> Home / Mock tests / Test on-materials / Subject Title
      // Skip the "subject" segment and show subject title instead of ID
      if (pathSegments[2] === 'subject' && pathSegments[3]) {
        // Add mock test breadcrumb if not already added
        if (pathSegments[1] && !breadcrumbs.some(b => b.target === `/mock-tests/${pathSegments[1]}`)) {
          const mockTestTarget = `/mock-tests/${pathSegments[1]}`;
          const mockTestTitle = pathSegments[1].replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          breadcrumbs.push({
            title: mockTestTitle,
            target: mockTestTarget,
            icon: faBookOpen
          });
        }
        
        // Add subject breadcrumb (skip "subject" segment, use subject title)
        const subjectTarget = `/mock-tests/${pathSegments[1]}/subject/${pathSegments[3]}`;
        const title = subjectTitle || `Subject ${pathSegments[3].slice(-4)}`;
        breadcrumbs.push({
          title: title,
          target: subjectTarget,
          icon: faBookOpen
        });
      }
    } else if (pathSegments[0] === 'dashboard') {
      // Dashboard pages
      breadcrumbs.push({
        title: 'Dashboard',
        target: '/dashboard',
        icon: faTachometerAlt
      });
    } else if (pathSegments[0] === 'about-us') {
      breadcrumbs.push({
        title: 'About Us',
        target: '/about-us',
        icon: faInfoCircle
      });
    } else if (pathSegments[0] === 'contact-1') {
      breadcrumbs.push({
        title: 'Contact',
        target: '/contact-1',
        icon: faEnvelope
      });
    } else if (pathSegments[0] === 'user-settings') {
      breadcrumbs.push({
        title: 'User Settings',
        target: '/user-settings',
        icon: faUser
      });
    } else {
      // Generic handling for other routes
      let currentPath = '';
      pathSegments.forEach((segment, index) => {
        currentPath += `/${segment}`;
        const title = segment.charAt(0).toUpperCase() + segment.slice(1).replace('-', ' ');
        breadcrumbs.push({
          title: title,
          target: currentPath,
          icon: getIconForBreadcrumb(title, currentPath)
        });
      });
    }

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  // Don't show breadcrumbs on home page
  if (pathname === '/') {
    return null;
  }

  return (
    <section 
      className="breadcrumbs py-5"
      style={{ 
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%)',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Subtle animated background pattern */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.03) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.02) 0%, transparent 50%)',
          pointerEvents: 'none'
        }}
      />
      
      <div className="container position-relative">
        <div className="row">
          <div className="col-12">
            <div 
              className="breadcrumbs__content d-flex align-items-center flex-wrap" 
              style={{ 
                listStyle: 'none', 
                margin: 0, 
                padding: '8px 0',
                minHeight: '48px'
              }}
            >
              {breadcrumbs.map((breadcrumb, index) => (
                <React.Fragment key={index}>
                  <div 
                    className="breadcrumbs__item enhanced-breadcrumb d-flex align-items-center"
                    style={{ 
                      listStyle: 'none', 
                      margin: 0, 
                      padding: 0,
                      position: 'relative'
                    }}
                  >
                    <Link 
                      to={breadcrumb.target}
                      className="breadcrumb-link d-flex align-items-center text-decoration-none"
                      style={{ 
                        color: '#ffffff',
                        padding: '10px 16px',
                        borderRadius: '12px',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        background: 'linear-gradient(145deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.12) 100%)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        backdropFilter: 'blur(10px)',
                        whiteSpace: 'nowrap',
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
                        fontFamily: '"Segoe UI", system-ui, -apple-system, sans-serif'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(145deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.25) 100%)';
                        e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.4), 0 0 20px rgba(255,255,255,0.1), inset 0 1px 0 rgba(255,255,255,0.3)';
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(145deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.12) 100%)';
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)';
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
                      }}
                    >
                      {/* Subtle shimmer effect */}
                      <div 
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: '-100%',
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
                          animation: 'shimmer 3s infinite',
                          pointerEvents: 'none'
                        }}
                      />
                      
                      <FontAwesomeIcon 
                        icon={breadcrumb.icon} 
                        className="breadcrumb-icon me-2" 
                        style={{ 
                          fontSize: '1rem',
                          color: '#ffffff',
                          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))',
                          transition: 'all 0.3s ease',
                          position: 'relative',
                          zIndex: 2
                        }}
                      />
                      <span style={{ 
                        fontWeight: '600',
                        fontSize: '0.95rem',
                        color: '#ffffff',
                        textShadow: '0 2px 4px rgba(0,0,0,0.6)',
                        letterSpacing: '0.3px',
                        position: 'relative',
                        zIndex: 2
                      }}>
                        {breadcrumb.title}
                      </span>
                    </Link>
                  </div>
                  {index < breadcrumbs.length - 1 && (
                    <div 
                      className="breadcrumb-separator d-flex align-items-center" 
                      style={{ 
                        margin: '0 12px',
                        position: 'relative'
                      }}
                    >
                      <div style={{
                        width: '24px',
                        height: '1px',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                        position: 'relative'
                      }}>
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: '4px',
                          height: '4px',
                          borderRadius: '50%',
                          background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.3) 70%)',
                          boxShadow: '0 0 6px rgba(255,255,255,0.4)'
                        }} />
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Add keyframe animation for shimmer effect */}
      <style>{`
        @keyframes shimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }
      `}</style>
    </section>
  );
} 